import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  DimensionEventStatus,
  DimensionOutroSummary,
  DimensionSummary,
} from "@dimensional-engineering/dimensional-models";

import styles from "./DimensionSummaries.module.css";

import {
  dimensionData,
  DimensionDataType,
  dimensionIcons,
  shortFormToLongFormDimensionSlugMap,
} from "@/assets/traitInfo/dimensionData";
import { useModalContext } from "@/context/ModalContext";
import DimensionSummariesModal from "./DimensionSummariesModal";
import DiscoveryGate from "@/components/shared/DiscoveryGate/DiscoveryGate";
import { summonProfileLink } from "@/components/shared/utils";
import { useAuth } from "@/context/AuthContext";
import PremiumPillIcon from "@/components/premium/PremiumPillIcon/PremiumPillIcon";
import AsessmentOutroScreen from "@/components/assessments-new/views/AsessmentOutroScreen/AsessmentOutroScreen";

type Props = {
  summary: DimensionDataType;
  unknown: boolean;
  onUnknownClick?: (slug: string) => void;
  updatedOn?: number;
};

export default function DimensionSummaryBlock(props: Props) {
  const { setModalComponent, setModalOpen } = useModalContext();
  const summary = useSelector(
    (state: RootState) => state.user.dimensionSummaries
  )?.find((s) => s.dimensionSlug === props.summary.slug);

  const outro = useGetDimensionOutroFromDimensionSummary({ summary: summary });

  if (props.unknown) {
    return (
      <div
        onClick={() => {
          props.onUnknownClick && props.onUnknownClick(props.summary.slug);
        }}
        className={styles.row}
      >
        <div className={styles.iconDiv}>
          {dimensionIcons[props.summary.slug].iconInactive}
        </div>
        <h2 className={styles.rowTitle} style={{ opacity: 0.3 }}>
          {props.summary.title}
        </h2>
      </div>
    );
  }

  return (
    <div
      onClick={() => {
        if (outro?.dimensionSummary?.deltaViews?.length) {
          return setModalComponent(
            <AsessmentOutroScreen
              onFinish={() => setModalOpen(false)}
              slug={props.summary.slug}
              outro={outro}
              modal
            />
          );
        } else {
          return setModalComponent(
            <DimensionSummariesModal slug={props.summary.slug} />
          );
        }
      }}
    >
      <div className={styles.row}>
        {outro?.dimensionSummary?.deltaViews?.length ? (
          <p className={styles.scoresUpdated}>Scores updated</p>
        ) : null}
        <div className={styles.iconDiv}>
          {dimensionIcons[props.summary.slug].icon}
        </div>

        <h2 className={styles.rowTitle}>{props.summary.title}</h2>
      </div>
    </div>
  );
}

export function DimensionSummaryResultsTabBlock(props: Props) {
  const { setModalComponent } = useModalContext();

  if (props.unknown) {
    return (
      <div
        onClick={() => {
          props.onUnknownClick && props.onUnknownClick(props.summary.slug);
        }}
        className={styles.resultsBlock}
      >
        <div className={styles.iconDiv}>
          {dimensionIcons[props.summary.slug].iconInactive}
        </div>
        <div style={{ opacity: 0.3 }}>
          <h3 className={styles.resultsDimensionText}>DIMENSION</h3>
          <h2 className={styles.resultTitle}>{props.summary.title}</h2>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() =>
        setModalComponent(<DimensionSummariesModal slug={props.summary.slug} />)
      }
      className={styles.resultsBlock}
    >
      {props.summary.slug === "coping-style" && (
        <div className={styles.premiumLogo}>
          <PremiumPillIcon />
        </div>
      )}
      <div className={styles.iconDiv}>
        {dimensionIcons[props.summary.slug].icon}
      </div>
      <div>
        <h3 className={styles.resultsDimensionText}>DIMENSION</h3>
        <h2 className={styles.resultTitle}>{props.summary.title}</h2>
      </div>
    </div>
  );
}

export function DimensionSummaryRenderer(props: { slug: string }) {
  const { setModalComponent, setModalOpen } = useModalContext();
  const { user } = useAuth();

  const dimension = dimensionData?.find((d) => d.slug === props.slug);
  const completedSlugs = useGetCompletedDimensionSlugs();
  if (!dimension) {
    return <></>;
  }

  return (
    <DimensionSummaryResultsTabBlock
      unknown={!completedSlugs.includes(props.slug)}
      onUnknownClick={() => {
        setModalComponent(
          <DiscoveryGate
            onClose={() => setModalOpen(false)}
            requiredDimensionSlugs={[props.slug]}
            onOpenShareLink={() => {
              summonProfileLink(
                user?.uid,
                "gating modal",
                "Invite to Dimensional"
              );
            }}
          />
        );
      }}
      summary={dimension}
    />
  );
}

function useGetCompletedDimensionSlugs() {
  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );
  const [completedSlugs, setCompletedSlugs] = useState<string[]>([]);

  useEffect(() => {
    let finishedSlugs: string[] = [];
    dimensionEvents?.forEach((e) => {
      if (
        e.event === DimensionEventStatus.complete ||
        e.event === DimensionEventStatus.reset
      ) {
        finishedSlugs.push(e.dimensionSlug);
      } else if (
        e.event === DimensionEventStatus.deferred ||
        e.event === DimensionEventStatus.deferredGated
      ) {
        const longFormEvent = dimensionEvents.find(
          (d) =>
            d.dimensionSlug ===
            shortFormToLongFormDimensionSlugMap[e.dimensionSlug]
        );
        if (
          longFormEvent?.event === DimensionEventStatus.complete ||
          longFormEvent?.event === DimensionEventStatus.reset
        ) {
          finishedSlugs.push(e.dimensionSlug);
        } else {
          if (e.event === DimensionEventStatus.deferred) {
            finishedSlugs.push(e.dimensionSlug);
          }
        }
      }
    });
    setCompletedSlugs(finishedSlugs);
  }, [dimensionEvents]);

  return completedSlugs;
}

function useGetDimensionOutroFromDimensionSummary(props: {
  summary: DimensionSummary | undefined;
}) {
  const [outro, setOutro] = useState<DimensionOutroSummary | null>(null);

  const elementsDiscovered = useSelector(
    (state: RootState) => state.user.elementsDiscovered
  );

  const patternsDiscovered = useSelector(
    (state: RootState) => state.user.patternsDiscovered
  );

  const archetypesDiscovered = useSelector(
    (state: RootState) => state.user.archetypesDiscovered
  );

  useEffect(() => {
    if (props.summary) {
      const elements = elementsDiscovered?.filter(
        (e) => e.dimensionSlug === props.summary?.dimensionSlug
      );
      const patterns = patternsDiscovered?.filter(
        (p) =>
          props.summary?.dimensionSlug &&
          p.dimensionSlugs.includes(props.summary?.dimensionSlug)
      );

      const archetypes = archetypesDiscovered?.filter(
        (a) => a.dimensionSlug === props.summary?.dimensionSlug
      );

      setOutro({
        elementDiscoveries: elements ?? [],
        dimensionSummary: props.summary,
        patternDiscoveries: patterns ?? [],
        archetypeDiscoveries: archetypes ?? [],
      });
    }
  }, [props.summary]);

  return outro;
}
